import React, { useState, useEffect } from "react";
import { PhoneIcon, DeleteIcon, MinusIcon, ClockIcon, ChevronLeftIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
export function PhoneDialer({
  isOpen,
  onClose
}: {
  isOpen?: boolean;
  onClose?: () => void;
}) {
  const [number, setNumber] = useState('');
  useEffect(() => {
    if (!isOpen) {
      setNumber('');
    }
  }, [isOpen]);
  const handlePress = (digit: string) => {
    if (number.length < 15) {
      setNumber(prev => prev + digit);
    }
  };
  const handleDelete = () => {
    setNumber(prev => prev.slice(0, -1));
  };
  const keypad = [{
    digit: '1',
    letters: ''
  }, {
    digit: '2',
    letters: 'ABC'
  }, {
    digit: '3',
    letters: 'DEF'
  }, {
    digit: '4',
    letters: 'GHI'
  }, {
    digit: '5',
    letters: 'JKL'
  }, {
    digit: '6',
    letters: 'MNO'
  }, {
    digit: '7',
    letters: 'PQRS'
  }, {
    digit: '8',
    letters: 'TUV'
  }, {
    digit: '9',
    letters: 'WXYZ'
  }, {
    digit: '*',
    letters: ''
  }, {
    digit: '0',
    letters: '+'
  }, {
    digit: '#',
    letters: ''
  }];
  return <AnimatePresence data-id="element-356" data-mp="2icd9kho1oo65">
      {isOpen && <div className="fixed inset-0 md:inset-auto md:bottom-6 md:right-6 z-[100] pointer-events-none md:origin-bottom-right" data-id="element-357" data-mp="2iblauxz5iabn">
          <motion.div initial={{
        y: 20,
        opacity: 0,
        scale: 0.95
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 20,
        opacity: 0,
        scale: 0.95
      }} className="w-full h-full md:w-[300px] md:h-auto bg-white md:rounded-xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto md:border border-gray-200" data-id="element-358" data-mp="2db5zgbyfjcwb">
            {/* Header */}
            <div className="bg-[#13E200] px-4 pt-12 md:pt-3 pb-5 relative shrink-0" data-id="element-359" data-mp="2w1x81balez9n">
              <div className="flex justify-between items-center mb-3" data-id="element-360" data-mp="2r45bg3z06oz4">
                <div className="flex items-center gap-2" data-id="element-361" data-mp="1kouxoxz375xx">
                  <button onClick={onClose} className="md:hidden text-black hover:bg-black/10 p-1 rounded transition-colors -ml-2" data-id="element-362" data-mp="1km8koitffy8b">
                    <ChevronLeftIcon className="w-6 h-6" data-id="element-363" data-mp="7pt42qfzqxmg" />
                  </button>
                  <span className="font-bold text-black text-sm" data-id="element-364" data-mp="bregybsizbwn">New call</span>
                </div>
                <button onClick={onClose} className="text-black hover:bg-black/10 p-1 rounded transition-colors" data-id="element-365" data-mp="3txsq7xfumxop">
                  <MinusIcon className="w-5 h-5" data-id="element-366" data-mp="2q6rmj7zllep" />
                </button>
              </div>
              <div className="bg-white/20 border border-black/10 rounded px-3 py-2" data-id="element-367" data-mp="3k77tkfjqk38f">
                <input type="text" value={number} readOnly placeholder="Enter a name or number" className="w-full bg-transparent text-black placeholder:text-black/50 focus:outline-none text-sm font-medium" data-id="element-368" data-mp="3oxy2g2rdeo7v" />
              </div>
              {/* Floating clock icon */}
              <button className="absolute -bottom-4 right-4 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center border border-gray-100 text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-369" data-mp="31ay8v9ebwefg">
                <ClockIcon className="w-4 h-4" data-id="element-370" data-mp="2ikbbt1aegyi4" />
              </button>
            </div>

            {/* Body */}
            <div className="pt-8 pb-8 px-6 flex-1 flex flex-col items-center justify-center bg-white" data-id="element-371" data-mp="2wjt1divisfsj">
              <div className="text-xs text-gray-500 mb-8 w-full text-center md:text-left pl-2" data-id="element-372" data-mp="2rkggv4s4fkt4">
                My caller ID: <span className="text-black font-medium ml-1" data-id="element-373" data-mp="35jzcwz7nr5p2">(205) 555-0198</span>
              </div>
              
              <div className="grid grid-cols-3 gap-y-4 gap-x-8 w-full max-w-[280px] mb-10" data-id="element-374" data-mp="31ie71xtjjrod">
                {keypad.map(key => <button key={key.digit} onClick={() => handlePress(key.digit)} className="w-16 h-16 md:w-16 md:h-16 rounded-full hover:bg-gray-100 flex flex-col items-center justify-center mx-auto transition-colors active:bg-gray-200" data-id="element-375" data-mp="3fcokhj037zf4">
                    <span className="text-3xl font-light text-black leading-none mb-1" data-id="element-376" data-mp="1hn01e4rlest0">{key.digit}</span>
                    {key.letters && <span className="text-[9px] text-gray-400 font-medium tracking-widest leading-none" data-id="element-377" data-mp="1mlb846mlcn70">{key.letters}</span>}
                  </button>)}
              </div>

              <div className="relative w-full flex justify-center mt-auto md:mt-0" data-id="element-378" data-mp="1wcfmp58cu61b">
                <button className="w-16 h-16 md:w-14 md:h-14 rounded-full bg-[#34a853] hover:bg-green-600 flex items-center justify-center shadow-md transition-transform active:scale-95" data-id="element-379" data-mp="yo5l2rchdkaq">
                  <PhoneIcon className="w-7 h-7 md:w-6 md:h-6 text-white fill-current" data-id="element-380" data-mp="13p1vlnrc84p4" />
                </button>
                {number && <button onClick={handleDelete} className="absolute right-8 md:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-2 transition-colors" data-id="element-381" data-mp="1dmg1itfmxb36">
                    <DeleteIcon className="w-8 h-8 md:w-6 md:h-6" data-id="element-382" data-mp="2oyqnogzvi8yn" />
                  </button>}
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}